/**
 * Editing one result row on the review page.
 *
 * The table shows a page's rows (pageWiseData) when the report has page data, and
 * extractedData.results otherwise. Page rows and results rows are separate copies with
 * different _ids, so the editor must load the exact row the table shows, and the save
 * must reach that row's copies only.
 */
import { ExtractedData, TestResult } from '../types';
import { readLimit, referenceRangeChange } from './referenceRangeEdit';

/** The rows the table shows for `currentPage` (the same rule the table uses). */
export function displayedRows(extractedData: ExtractedData | undefined, currentPage: number): TestResult[] {
  const pages = extractedData?.pageWiseData;
  if (pages && pages.length > 0) {
    return pages.find((page) => page.pageNumber === currentPage)?.results || [];
  }
  return extractedData?.results || [];
}

/** The edit form's starting values: exactly the row being shown, limits included (0 stays 0). */
export interface RowEditForm {
  value: string;
  unit: string;
  refLow: number | string;
  refHigh: number | string;
  refRange: string;
}

export function editFormFromRow(row: TestResult): RowEditForm {
  const range = (row.referenceRange || row.normalRange) as
    | { low?: number | null; high?: number | null; min?: number; max?: number; referenceRange?: string }
    | undefined;
  return {
    value: row.value,
    unit: row.unit,
    refLow: range?.low ?? range?.min ?? '',
    refHigh: range?.high ?? range?.max ?? '',
    refRange: range?.referenceRange ?? '',
  };
}

export interface RowUpdate {
  field: 'value' | 'unit' | 'referenceRange';
  newValue: unknown;
}

/**
 * Only what the nurse changed, compared with the row being shown: an untouched field is
 * never sent, so its stored value (a limit of 0 included) is kept.
 */
export function rowUpdates(row: TestResult, edited: Partial<Record<keyof RowEditForm, unknown>>): RowUpdate[] {
  const updates: RowUpdate[] = [];
  if (edited.value !== undefined && edited.value !== row.value) updates.push({ field: 'value', newValue: edited.value });
  if (edited.unit !== undefined && edited.unit !== row.unit) updates.push({ field: 'unit', newValue: edited.unit });

  const form = editFormFromRow(row);
  const current = { low: readLimit(form.refLow), high: readLimit(form.refHigh), text: form.refRange };
  const range = referenceRangeChange(current, {
    low: edited.refLow === undefined ? form.refLow : edited.refLow,
    high: edited.refHigh === undefined ? form.refHigh : edited.refHigh,
    text: edited.refRange === undefined ? form.refRange : edited.refRange,
  });
  if (range) updates.push({ field: 'referenceRange', newValue: range });
  return updates;
}

/**
 * Which stored rows an edit or delete of `row` would reach.
 *
 * Nothing stored links a page row to its results row (separate copies, different _ids,
 * no page or position field). The backend finds the row by name: PUT /review/:id/parameter
 * takes the first results row with that serviceItemName and the first such row on EVERY
 * page; DELETE /review/:id/parameter/:name also matches a row's `name`. So the action is
 * safe only when the shown row's name picks out one row in results and one row across all
 * pages. Anything else is refused rather than guessed.
 */
export type RowTarget = { ok: true; parameterName: string } | { ok: false; reason: 'ambiguous' | 'not_found' };

export function rowTarget(
  extractedData: ExtractedData | undefined,
  row: Pick<TestResult, 'serviceItemName' | 'value'>,
  action: 'edit' | 'delete'
): RowTarget {
  const name = row.serviceItemName;
  if (!name) return { ok: false, reason: 'not_found' };
  // The delete endpoint also matches a row's `name` field
  const matches = (candidate: TestResult) =>
    candidate.serviceItemName === name || (action === 'delete' && candidate.name === name);

  const inResults = (extractedData?.results || []).filter(matches);
  const pages = extractedData?.pageWiseData || [];
  const onPages = pages.reduce((count, page) => count + (page.results || []).filter(matches).length, 0);
  if (inResults.length > 1 || onPages > 1) return { ok: false, reason: 'ambiguous' };

  if (action === 'edit') {
    // The edit endpoint needs the results row; on a page it must also be this row's copy
    if (inResults.length === 0) return { ok: false, reason: 'not_found' };
    if (pages.length > 0 && inResults[0].value !== row.value) return { ok: false, reason: 'not_found' };
  }
  return { ok: true, parameterName: name };
}

/** Shown when an edit or delete is refused (nothing was sent). */
export function rowTargetMessage(reason: 'ambiguous' | 'not_found'): string {
  return reason === 'ambiguous'
    ? 'This row has the same name as another row, so it cannot be changed safely here. No changes were made.'
    : 'This row could not be matched to the saved results, so it cannot be changed safely here. No changes were made.';
}

/** The exact rows sent to the backend (R16): it acts on these ids, never on a name. */
export interface RowIds {
  resultRowId?: string;
  pageNumber?: number;
  pageRowId?: string;
}

export type ReviewRowRef =
  | { ok: true; parameterName: string; resultRowId: string; ids: RowIds }
  | { ok: false; reason: 'ambiguous' | 'not_found' };

/**
 * The rows an edit or delete of the shown row acts on.
 * - A report without page data: the shown row is the results row.
 * - A page row linked by rowKey (reports processed from R16 on): its results row is the
 *   one with the same rowKey, so rows that share a name are told apart.
 * - A page row without a link (older reports): paired with its results row by name, only
 *   when that name picks out one row (rowTarget); otherwise refused.
 */
export function reviewRowRef(
  extractedData: ExtractedData | undefined,
  row: TestResult,
  pageNumber: number
): ReviewRowRef {
  const results = extractedData?.results || [];
  const pages = extractedData?.pageWiseData || [];
  if (!row._id) return { ok: false, reason: 'not_found' };

  if (pages.length === 0) {
    const own = results.find((candidate) => candidate._id === row._id);
    return own && own._id
      ? { ok: true, parameterName: own.serviceItemName, resultRowId: own._id, ids: { resultRowId: own._id } }
      : { ok: false, reason: 'not_found' };
  }

  const pageIds = { pageNumber, pageRowId: row._id };
  if (row.rowKey) {
    const linked = results.find((candidate) => candidate.rowKey === row.rowKey);
    // Linked but gone from results (excluded or deduplicated): nothing to change there
    if (!linked || !linked._id) return { ok: false, reason: 'not_found' };
    return { ok: true, parameterName: row.serviceItemName, resultRowId: linked._id, ids: { resultRowId: linked._id, ...pageIds } };
  }

  const target = rowTarget(extractedData, row, 'edit');
  if (!target.ok) return target;
  const named = results.find((candidate) => candidate.serviceItemName === target.parameterName);
  if (!named || !named._id) return { ok: false, reason: 'not_found' };
  return { ok: true, parameterName: target.parameterName, resultRowId: named._id, ids: { resultRowId: named._id, ...pageIds } };
}

/**
 * For the mapping chip of the shown row: its results row (index, mapping) and the ids to
 * send, when the row is known exactly (no page data, or linked by rowKey). Null for an
 * unlinked page row: the chip then keeps finding its row as before (by name and value),
 * and the backend refuses that when another row has the same name and value.
 */
export function mappingRowRef(
  extractedData: ExtractedData | undefined,
  row: TestResult,
  pageNumber: number
): { resultIndex: number; resultRow: TestResult; ids: RowIds } | null {
  const results = extractedData?.results || [];
  const pages = extractedData?.pageWiseData || [];
  const resultIndex =
    pages.length === 0
      ? results.findIndex((candidate) => !!row._id && candidate._id === row._id)
      : row.rowKey
      ? results.findIndex((candidate) => candidate.rowKey === row.rowKey)
      : -1;
  const resultRow = results.find((_, index) => index === resultIndex);
  if (!resultRow || !resultRow._id) return null;
  const ids: RowIds =
    pages.length === 0 ? { resultRowId: resultRow._id } : { resultRowId: resultRow._id, pageNumber, pageRowId: row._id };
  return { resultIndex, resultRow, ids };
}
